/**
 * Assign homework — the side-pane flow, in the order a teacher thinks in:
 *
 *   1. Who      one student, or a whole batch
 *   2. Subject  one of the student's courses
 *   3. What     a rich-text instruction, and/or topics picked from the subject's tree
 *
 * The tree icon beside "What to do" opens the subject's content. Tick a topic and a
 * description box appears against it — each ticked topic becomes its OWN homework, so one
 * can be signed off while another is still outstanding.
 */
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import RichTextEditor from '../richtext/RichTextEditor';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { toast } from 'sonner';
import {
  Student, UsersThree, BookOpen, TreeStructure, CaretDown, CaretRight,
  Check, X,
} from '@phosphor-icons/react';

const KIND_ICON = { section: 'Chapter', subsection: 'Topic' };

/** Flatten the course tree, remembering depth so the list can be indented. */
function flatten(nodes, depth = 0, out = []) {
  (nodes || []).forEach((n) => {
    out.push({ ...n, depth });
    if (n.children?.length) flatten(n.children, depth + 1, out);
  });
  return out;
}

function TopicRow({ node, picked, description, onToggle, onDescribe, expanded, onExpand, hasChildren }) {
  return (
    <div className="border-b border-border last:border-b-0" data-testid={`topic-row-${node.id}`}>
      <div className="flex items-start gap-2 px-2 py-1.5 hover:bg-muted/40"
        style={{ paddingLeft: `${8 + node.depth * 16}px` }}>
        {hasChildren ? (
          <button type="button" onClick={onExpand} className="mt-0.5 text-muted-foreground hover:text-foreground"
            title={expanded ? 'Collapse' : 'Expand'} data-testid={`expand-${node.id}`}>
            {expanded ? <CaretDown className="w-3.5 h-3.5" /> : <CaretRight className="w-3.5 h-3.5" />}
          </button>
        ) : <span className="w-3.5" />}
        <label className="flex items-start gap-2 cursor-pointer min-w-0 flex-1">
          <input type="checkbox" checked={picked} onChange={onToggle} className="mt-1"
            data-testid={`pick-topic-${node.id}`} />
          <span className="min-w-0">
            <span className="block text-[13px] truncate">{node.title}</span>
            <span className="block text-[10px] uppercase tracking-wide text-muted-foreground">
              {KIND_ICON[node.node_type] || 'Subtopic'}
            </span>
          </span>
        </label>
      </div>
      {picked && (
        <div className="px-2 pb-2" style={{ paddingLeft: `${34 + node.depth * 16}px` }}>
          <Input value={description || ''} onChange={(e) => onDescribe(e.target.value)}
            placeholder="What to do for this topic…" className="text-[12px]"
            data-testid={`topic-desc-${node.id}`} />
        </div>
      )}
    </div>
  );
}

export default function AssignHomework({ api, students = [], courses = [], defaultStudentId, date, onDone, onCancel }) {
  const [mode, setMode] = useState('student');          // student | batch
  const [studentId, setStudentId] = useState(defaultStudentId || students[0]?.id || '');
  const [batches, setBatches] = useState([]);
  const [batchId, setBatchId] = useState('');
  const [courseId, setCourseId] = useState('');
  const [dueDate, setDueDate] = useState('');
  const [instructions, setInstructions] = useState('');
  const [treeOpen, setTreeOpen] = useState(false);
  const [tree, setTree] = useState(null);
  const [treeLoading, setTreeLoading] = useState(false);
  const [collapsed, setCollapsed] = useState({});
  const [picked, setPicked] = useState({});             // node id → description
  const [saving, setSaving] = useState(false);

  // Batches only matter when assigning to a whole class.
  useEffect(() => {
    if (mode !== 'batch') return;
    api.get('/attendance/batches')
      .then((r) => {
        setBatches(r.data || []);
        setBatchId((cur) => cur || r.data?.[0]?.id || '');
      })
      .catch(() => setBatches([]));
  }, [api, mode]);

  const loadTree = useCallback(async () => {
    if (!courseId) { setTree(null); return; }
    setTreeLoading(true);
    try {
      const r = await api.get(`/courses/${courseId}/tree`);
      setTree(r.data?.tree || r.data?.nodes || []);
    } catch {
      setTree([]);
      toast.error('Could not load the subject’s contents');
    } finally {
      setTreeLoading(false);
    }
  }, [api, courseId]);
  useEffect(() => { if (treeOpen) loadTree(); }, [treeOpen, loadTree]);
  useEffect(() => { setPicked({}); setTree(null); }, [courseId]);

  const flat = useMemo(() => flatten(tree || []), [tree]);
  const visible = useMemo(() => {
    const hidden = new Set();
    const out = [];
    flat.forEach((n) => {
      const parentHidden = n.parent_id && hidden.has(n.parent_id);
      if (parentHidden || (n.parent_id && collapsed[n.parent_id])) {
        hidden.add(n.id);
        return;
      }
      out.push(n);
    });
    return out;
  }, [flat, collapsed]);

  const hasChildren = useCallback((id) => flat.some((n) => n.parent_id === id), [flat]);
  const pickedIds = Object.keys(picked);

  const submit = async () => {
    if (mode === 'student' && !studentId) { toast.error('Choose a student.'); return; }
    if (mode === 'batch' && !batchId) { toast.error('Choose a batch.'); return; }
    const items = pickedIds.map((id) => {
      const node = flat.find((n) => n.id === id);
      return { node_id: id, title: node?.title || 'Topic', description: picked[id] || '' };
    });
    if (!items.length && !instructions.replace(/<[^>]+>/g, '').trim()) {
      toast.error('Say what the homework is, or pick topics from the subject.');
      return;
    }
    setSaving(true);
    try {
      const res = await api.post('/diary/homework/assign', {
        student_ids: mode === 'student' ? [studentId] : [],
        batch_id: mode === 'batch' ? batchId : null,
        course_id: courseId || null,
        due_date: dueDate || null,
        instructions,
        items,
        date,
      });
      const d = res.data;
      toast.success(d.students > 1
        ? `Assigned ${d.per_student} item(s) to ${d.students} students`
        : `Assigned ${d.created} homework item${d.created === 1 ? '' : 's'}`);
      onDone?.();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Could not assign the homework');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="p-3 space-y-4" data-testid="assign-homework-pane">
      {/* ---------------------------------------------------------- 1. who */}
      <section className="space-y-2">
        <Label className="text-[11px] uppercase tracking-wide text-muted-foreground">1 · Who is it for</Label>
        <div className="flex gap-1.5">
          {[['student', 'One student', Student], ['batch', 'A whole batch', UsersThree]].map(([key, label, Icon]) => (
            <button key={key} type="button" onClick={() => setMode(key)}
              className={`flex-1 inline-flex items-center justify-center gap-1.5 h-8 rounded-md border text-[12px] ${mode === key ? 'border-shell-primary bg-shell-primary/5 font-medium' : 'border-border text-muted-foreground hover:bg-muted/40'}`}
              data-testid={`hw-mode-${key}`}>
              <Icon className="w-4 h-4" />{label}
            </button>
          ))}
        </div>
        {mode === 'student' ? (
          <select className="form-select w-full" value={studentId} onChange={(e) => setStudentId(e.target.value)} data-testid="hw-student">
            {students.length === 0 && <option value="">No students</option>}
            {students.map((s) => <option key={s.id} value={s.id}>{s.name}{s.member_id ? ` · ${s.member_id}` : ''}</option>)}
          </select>
        ) : (
          <select className="form-select w-full" value={batchId} onChange={(e) => setBatchId(e.target.value)} data-testid="hw-batch">
            {batches.length === 0 && <option value="">No batches</option>}
            {batches.map((b) => <option key={b.id} value={b.id}>{b.name} · {b.student_count} student{b.student_count === 1 ? '' : 's'}</option>)}
          </select>
        )}
        {mode === 'batch' && batchId && (
          <p className="field-hint">Every student in the batch gets their own copy, so each can be signed off separately.</p>
        )}
      </section>

      {/* ---------------------------------------------------------- 2. subject */}
      <section className="space-y-2">
        <Label className="text-[11px] uppercase tracking-wide text-muted-foreground">2 · Subject</Label>
        <select className="form-select w-full" value={courseId} onChange={(e) => setCourseId(e.target.value)} data-testid="hw-subject">
          <option value="">— no particular subject —</option>
          {courses.map((c) => <option key={c.id} value={c.id}>{c.title}</option>)}
        </select>
      </section>

      {/* ---------------------------------------------------------- 3. what */}
      <section className="space-y-2">
        <div className="flex items-center gap-2">
          <Label className="text-[11px] uppercase tracking-wide text-muted-foreground mr-auto">3 · What to do</Label>
          <Button type="button" variant={treeOpen ? 'default' : 'outline'} size="sm" className="h-7 text-[12px]"
            disabled={!courseId} onClick={() => setTreeOpen((v) => !v)}
            title={courseId ? 'Pick topics from this subject' : 'Choose a subject first'}
            data-testid="hw-tree-toggle">
            <TreeStructure className="w-4 h-4" />{treeOpen ? 'Hide topics' : 'Pick topics'}
          </Button>
        </div>

        <RichTextEditor value={instructions} onChange={setInstructions} compact minHeight={110}
          placeholder="Instructions for the whole assignment…" testid="hw-instructions" />

        {treeOpen && (
          <div className="rounded-md border border-border overflow-hidden" data-testid="hw-tree">
            <div className="flex items-center gap-2 px-2 h-8 bg-muted/40 border-b border-border">
              <BookOpen className="w-3.5 h-3.5 text-shell-primary" />
              <span className="text-[12px] font-medium mr-auto">
                {courses.find((c) => c.id === courseId)?.title || 'Subject'}
              </span>
              {pickedIds.length > 0 && (
                <span className="text-[11px] text-muted-foreground" data-testid="hw-picked-count">
                  {pickedIds.length} selected
                </span>
              )}
            </div>
            <div className="max-h-[320px] overflow-y-auto">
              {treeLoading && <p className="p-3 text-[12px] text-muted-foreground">Loading…</p>}
              {!treeLoading && visible.length === 0 && (
                <p className="p-3 text-[12px] text-muted-foreground" data-testid="hw-tree-empty">
                  This subject has no content yet.
                </p>
              )}
              {!treeLoading && visible.map((n) => (
                <TopicRow key={n.id} node={n}
                  picked={n.id in picked}
                  description={picked[n.id]}
                  hasChildren={hasChildren(n.id)}
                  expanded={!collapsed[n.id]}
                  onExpand={() => setCollapsed((c) => ({ ...c, [n.id]: !c[n.id] }))}
                  onToggle={() => setPicked((p) => {
                    const next = { ...p };
                    if (n.id in next) delete next[n.id]; else next[n.id] = '';
                    return next;
                  })}
                  onDescribe={(v) => setPicked((p) => ({ ...p, [n.id]: v }))}
                />
              ))}
            </div>
            {pickedIds.length > 0 && (
              <p className="px-2 py-1.5 bg-muted/30 text-[11px] text-muted-foreground">
                Each selected topic becomes its own homework item.
              </p>
            )}
          </div>
        )}
      </section>

      <section className="space-y-2">
        <div className="field">
          <Label htmlFor="hw-due">Due date</Label>
          <Input id="hw-due" type="date" value={dueDate} onChange={(e) => setDueDate(e.target.value)} data-testid="hw-due" />
        </div>
      </section>

      <div className="flex items-center gap-2 pt-1 border-t border-border">
        <span className="text-[11px] text-muted-foreground mr-auto" data-testid="hw-summary">
          {pickedIds.length ? `${pickedIds.length} item(s)` : '1 item'}
          {mode === 'batch' && batchId
            ? ` × ${batches.find((b) => b.id === batchId)?.student_count || 0} students`
            : ''}
        </span>
        <Button variant="outline" size="sm" onClick={onCancel}><X className="w-4 h-4" /> Cancel</Button>
        <Button size="sm" onClick={submit} disabled={saving} data-testid="hw-assign">
          <Check className="w-4 h-4" /> {saving ? 'Assigning…' : 'Assign'}
        </Button>
      </div>
    </div>
  );
}

